import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Wallet, Plus, Trash2, Pencil, MessageCircle, X } from 'lucide-react';
import { useLanguage } from './LanguageContext';
import { portfolioDldValue, planPaidToDate, usePmUnits, leaseBook, isOffplanUnit } from './portfolioMetrics';
import { useAirbnbIncome, activeMonths } from '../portals/owner/AirbnbIncome';
import { cachedMarketIntelligence, storedMarketIntelligence } from './PortfolioView';
import './WealthOverview.css';

// ============================================================
// MEIN GESAMTVERMÖGEN — the whole wealth, Michael's Co-Pilot concept (v1).
//
// Dubai property comes from the app itself (each held unit at its DLD value,
// portfolioDldValue — the same rule as every other screen) and the developer
// instalments still open from the payment plans (planPaidToDate). Everything
// else is entered by the investor (Backend/services/wealth.js): their data,
// editable and deletable by them, not verified by us — the screen says so.
// Currencies are converted with the ECB rate the server returns. The insights
// are arithmetic on these figures, not advice; securities and funds are never
// recommended (regulated investment advice) — that goes to an advisor.
// ============================================================

interface Item { id: string; kind: 'asset' | 'liability'; category: string; label: string; amount: number; currency: string }
interface Fx { date: string; aedPerEur: number; aedPerChf: number }

interface Props {
  investorId?: string;
  units: any[];
  apiUrl: string;
  demoMode?: boolean;
  onAsk: (message: string) => void;
  /** Showcase: Property Management fixtures for the lease book. */
  pmUnitsOverride?: any[];
}

const AED_PER_USD = 3.6725;
const CATS: Record<string, [string, string]> = {
  dubai: ['Dubai-Immobilien (DLD-Wert)', 'Dubai property (DLD value)'],
  cash: ['Bank & Bargeld', 'Bank & cash'],
  securities: ['Wertpapiere & Fonds', 'Securities & funds'],
  company: ['Unternehmensbeteiligungen', 'Company holdings'],
  metals: ['Edelmetalle', 'Precious metals'],
  realestate: ['Weitere Immobilien', 'Other real estate'],
  pension: ['Versicherungen & Altersvorsorge', 'Insurance & pensions'],
  other: ['Sonstiges', 'Other'],
  instalments: ['Offene Bauträger-Raten (Dubai)', 'Open developer instalments (Dubai)'],
  mortgage: ['Hypotheken', 'Mortgages'],
  loan: ['Kredite', 'Loans'],
  otherdebt: ['Sonstige Verbindlichkeiten', 'Other liabilities'],
};
const ASSET_CATS = ['cash', 'securities', 'company', 'metals', 'realestate', 'pension', 'other'];
const LIAB_CATS = ['mortgage', 'loan', 'otherdebt'];
const CURRENCIES = ['EUR', 'AED', 'CHF', 'USD'];
// Bar shades: gold, black and white only, as alpha steps.
const SHADES = ['#D6B160', '#000000', 'rgba(214,177,96,0.6)', 'rgba(0,0,0,0.55)', 'rgba(214,177,96,0.35)', 'rgba(0,0,0,0.3)', 'rgba(214,177,96,0.18)', 'rgba(0,0,0,0.15)'];

const WealthOverview: React.FC<Props> = ({ investorId, units, apiUrl, demoMode, onAsk, pmUnitsOverride }) => {
  const { language } = useLanguage();
  const de = language === 'de';
  const locale = de ? 'de-DE' : 'en-GB';
  // Every amount on the page goes through here: totals are kept in EUR and
  // shown in the currency the owner picks (AED at the same ECB rate), so the
  // page never mixes the two.
  const eurFmt = (n: number) => {
    if (showCur === 'AED' && fx?.aedPerEur) {
      const v = Math.round(n * fx.aedPerEur).toLocaleString(locale);
      return de ? `${v} AED` : `AED ${v}`;
    }
    return `${Math.round(n).toLocaleString(locale)} €`;
  };
  const pctFmt = (n: number) => `${n.toLocaleString(locale, { maximumFractionDigits: 0 })} %`;
  const cat = (c: string) => (CATS[c] ? CATS[c][de ? 0 : 1] : c);

  const [items, setItems] = useState<Item[]>([]);
  const [fx, setFx] = useState<Fx | null>(null);
  const [plans, setPlans] = useState<any[]>([]);
  const [loading, setLoading] = useState(!demoMode);
  const [editing, setEditing] = useState<Partial<Item> | null>(null);
  const [showCur, setShowCur] = useState<'EUR' | 'AED'>(() => {
    try { return localStorage.getItem('wealthCurrency') === 'AED' ? 'AED' : 'EUR'; } catch { return 'EUR'; }
  });
  const pickCur = (c: 'EUR' | 'AED') => {
    setShowCur(c);
    try { localStorage.setItem('wealthCurrency', c); } catch { /* per-device preference only */ }
  };
  const [saving, setSaving] = useState(false);

  const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('authToken') || ''}` });
  const load = useCallback(async () => {
    if (demoMode || !investorId || !apiUrl) { setLoading(false); return; }
    const [w, p] = await Promise.all([
      fetch(`${apiUrl}/wealth`, { headers: auth() }).then(r => (r.ok ? r.json() : null)).catch(() => null),
      fetch(`${apiUrl}/installment-plans/${encodeURIComponent(investorId)}`, { headers: auth() }).then(r => (r.ok ? r.json() : [])).catch(() => []),
    ]);
    if (w?.success) { setItems(w.items || []); setFx(w.fx || null); }
    setPlans(Array.isArray(p) ? p : []);
    setLoading(false);
  }, [apiUrl, investorId, demoMode]);
  useEffect(() => { load(); }, [load]);

  // Dubai property: the app's own rule (held units at DLD value, else purchase price).
  const dld = useMemo(() => (investorId ? (cachedMarketIntelligence(investorId) || storedMarketIntelligence(investorId)) : null), [investorId]);
  const dubai = useMemo(() => portfolioDldValue(dld, units || []), [dld, units]);
  // Open instalments on units still held (a plan whose unit was sold or removed is not owed).
  const openInstalmentsAed = useMemo(() => {
    const held = new Set((units || []).filter((u: any) => (u?.status || '').toLowerCase() !== 'sold').map((u: any) => String(u.id)));
    return plans.filter((pl: any) => held.has(String(pl?.unitId))).reduce((s: number, pl: any) => s + planPaidToDate(pl).open, 0);
  }, [plans, units]);

  // ── Ongoing income ─────────────────────────────────────────────────────
  // Long-term rent: the SAME lease book as the Home rental card (leaseBook),
  // contract rent and net after booked costs + one year of service charge.
  const pmUnits = usePmUnits(investorId, pmUnitsOverride);
  const book = useMemo(() => leaseBook(pmUnits), [pmUnits]);
  // Airbnb: DreamHaven's reports, the module's own rule — the last 12 CLOSED
  // months of letting, net to the owner. Nothing when the feed is off.
  const airbnb = useAirbnbIncome((units || []).map((u: any) => String(u.id)), demoMode);
  const airbnbTotals = useMemo(() => {
    let net = 0, booked = 0, avail = 0, months = 0;
    let since: string | null = null; // first month with a booked night, across the apartments
    (airbnb.units || []).forEach((u) => {
      const active = activeMonths(u.months || []);
      const closed = active.filter((m) => m.status === 'final').slice(0, 12);
      closed.forEach((m) => { net += m.net; booked += m.bookedNights; avail += m.availableNights; });
      months = Math.max(months, closed.length);
      const first = active.length ? active[active.length - 1].month : null;
      if (first && (!since || first < since)) since = first;
    });
    return { units: (airbnb.units || []).length, net, occupancy: avail > 0 ? Math.round((booked / avail) * 100) : null, months, since };
  }, [airbnb]);

  const toEur = useCallback((amount: number, currency: string): number | null => {
    if (currency === 'EUR') return amount;
    if (!fx?.aedPerEur) return null;
    const aed = currency === 'AED' ? amount : currency === 'USD' ? amount * AED_PER_USD : currency === 'CHF' ? amount * fx.aedPerChf : null;
    return aed === null ? null : aed / fx.aedPerEur;
  }, [fx]);

  const totals = useMemo(() => {
    const byCat: Record<string, number> = {};
    const add = (c: string, v: number | null) => { if (v !== null && v > 0) byCat[c] = (byCat[c] || 0) + v; };
    add('dubai', toEur(dubai.value, 'AED'));
    add('instalments', toEur(openInstalmentsAed, 'AED'));
    let aedLinked = toEur(dubai.value, 'AED') || 0;   // AED is pegged to the USD
    items.forEach(i => {
      const v = toEur(i.amount, i.currency);
      add(i.category, v);
      if (i.kind === 'asset' && v !== null && (i.currency === 'AED' || i.currency === 'USD')) aedLinked += v;
    });
    const assetsList = ['dubai', ...ASSET_CATS].map(c => [c, byCat[c] || 0] as [string, number]).filter(([, v]) => v > 0);
    const liabList = ['instalments', ...LIAB_CATS].map(c => [c, byCat[c] || 0] as [string, number]).filter(([, v]) => v > 0);
    const assets = assetsList.reduce((s, [, v]) => s + v, 0);
    const liabilities = liabList.reduce((s, [, v]) => s + v, 0);
    return { assetsList, liabList, assets, liabilities, net: assets - liabilities, aedLinked };
  }, [items, dubai.value, openInstalmentsAed, toEur]);

  const share = (v: number) => (totals.assets > 0 ? (v / totals.assets) * 100 : 0);
  const dubaiShare = share(totals.assetsList.find(([c]) => c === 'dubai')?.[1] || 0);
  const cashShare = share(totals.assetsList.find(([c]) => c === 'cash')?.[1] || 0);
  const debtRatio = totals.assets > 0 ? (totals.liabilities / totals.assets) * 100 : 0;
  const usdAedShare = share(totals.aedLinked);
  const hasOther = items.length > 0;

  const rentNetEur = book.rows.length ? toEur(book.net, 'AED') : null;
  const rentGrossEur = book.rows.length ? toEur(book.rent, 'AED') : null;
  const airbnbEur = airbnbTotals.units && airbnbTotals.months ? toEur(airbnbTotals.net, 'AED') : null;
  const incomeEur = (rentNetEur || 0) + (airbnbEur || 0);

  // Where the income comes from, so 18 units are never read as 18 earners:
  // long-term lease, Airbnb only, under construction, nothing on record.
  const mix = useMemo(() => {
    const held = (units || []).filter((u: any) => (u?.status || '').toLowerCase() !== 'sold');
    const leaseIds = new Set(book.rows.map((r: any) => String(r.unitId)));
    const airbnbIds = new Set((airbnb.units || []).map((u: any) => String(u.unitId)));
    let lease = 0, airbnbOnly = 0, building = 0, none = 0;
    held.forEach((u: any) => {
      const id = String(u.id);
      if (leaseIds.has(id)) lease++;
      else if (airbnbIds.has(id)) airbnbOnly++;
      else if (isOffplanUnit(u)) building++;
      else none++;
    });
    return { held: held.length, lease, airbnbOnly, building, none };
  }, [units, book, airbnb]);
  // Airbnb history can be shorter than a year (new listings): the total then
  // says which period each part covers instead of calling it "per year".
  const airbnbShort = airbnbEur !== null && airbnbTotals.months < 12;
  const sinceLabel = airbnbTotals.since
    ? new Date(`${airbnbTotals.since}-01T00:00:00`).toLocaleDateString(de ? 'de-DE' : 'en-GB', { month: 'long', year: 'numeric' })
    : null;

  const insights: string[] = [];
  if (incomeEur > 0 && totals.net > 0) {
    const onNet = (incomeEur / totals.net) * 100;
    const earners = mix.lease + mix.airbnbOnly;
    insights.push(de
      ? `${earners} von ${mix.held} Einheiten erzielen derzeit Einnahmen (${mix.lease} langfristig vermietet, ${mix.airbnbOnly} nur über Airbnb). Zusammen rund ${eurFmt(incomeEur)} netto${airbnbShort ? ` (Airbnb nur die letzten ${airbnbTotals.months} Monate)` : ' im Jahr'} – ${onNet.toLocaleString(locale, { maximumFractionDigits: 1 })} % Ihres Nettovermögens.`
      : `${earners} of ${mix.held} units currently earn income (${mix.lease} on long-term leases, ${mix.airbnbOnly} on Airbnb only). Together about ${eurFmt(incomeEur)} net${airbnbShort ? ` (Airbnb only the last ${airbnbTotals.months} months)` : ' a year'} — ${onNet.toLocaleString(locale, { maximumFractionDigits: 1 })}% of your net worth.`);
    if (mix.none > 0) {
      insights.push(de
        ? `Für ${mix.none} fertige ${mix.none === 1 ? 'Einheit ist' : 'Einheiten sind'} keine Miete erfasst. Ist eine davon vermietet, laden Sie den Mietvertrag hoch – dann rechnet der Co-Pilot sie mit.`
        : `${mix.none} completed ${mix.none === 1 ? 'unit has' : 'units have'} no rent on record. If one is let, upload the lease — the Co-Pilot then counts it.`);
    }
  }
  if (hasOther && totals.assets > 0) {
    insights.push(dubaiShare > 50
      ? (de ? `${pctFmt(dubaiShare)} Ihres Vermögens liegen in Dubai-Immobilien – eine deutliche Konzentration in einem Markt.` : `${pctFmt(dubaiShare)} of your wealth is Dubai property — a clear concentration in one market.`)
      : (de ? `${pctFmt(dubaiShare)} Ihres Vermögens liegen in Dubai-Immobilien.` : `${pctFmt(dubaiShare)} of your wealth is Dubai property.`));
    insights.push(cashShare < 5
      ? (de ? `Nur ${pctFmt(cashShare)} sind als Bank- und Bargeld sofort verfügbar. Als Leitlinie gilt: Reserven für 3–5 Jahre Marktschwankung einplanen.` : `Only ${pctFmt(cashShare)} is immediately available as bank and cash. Guideline: plan reserves for 3–5 years of market swings.`)
      : (de ? `${pctFmt(cashShare)} sind als Bank- und Bargeld sofort verfügbar.` : `${pctFmt(cashShare)} is immediately available as bank and cash.`));
    insights.push(de
      ? `${pctFmt(usdAedShare)} Ihres Vermögens hängen am Dirham bzw. US-Dollar (der Dirham ist an den Dollar gekoppelt), der Rest an Euro und Franken.`
      : `${pctFmt(usdAedShare)} of your wealth is tied to the dirham / US dollar (the dirham is pegged to the dollar), the rest to euro and franc.`);
    if (totals.liabilities > 0) insights.push(de ? `Verbindlichkeiten entsprechen ${pctFmt(debtRatio)} Ihres Vermögens.` : `Liabilities equal ${pctFmt(debtRatio)} of your assets.`);
  }

  const save = async () => {
    if (!editing) return;
    const amount = Number(String(editing.amount ?? '').replace(/[^\d.]/g, ''));
    if (!(amount > 0)) return;
    const item = { ...editing, amount, kind: editing.kind || 'asset', currency: editing.currency || 'EUR', category: editing.category || (editing.kind === 'liability' ? 'loan' : 'cash') };
    if (demoMode) {
      const id = item.id || `demo_${Date.now()}`;
      setItems(prev => [...prev.filter(i => i.id !== id), { ...(item as Item), id }]);
      setEditing(null);
      return;
    }
    setSaving(true);
    await fetch(`${apiUrl}/wealth/items`, { method: 'PUT', headers: { ...auth(), 'Content-Type': 'application/json' }, body: JSON.stringify(item) }).catch(() => null);
    setSaving(false);
    setEditing(null);
    load();
  };
  const del = async (id: string) => {
    if (!window.confirm(de ? 'Diesen Eintrag löschen?' : 'Delete this entry?')) return;
    if (demoMode) { setItems(prev => prev.filter(i => i.id !== id)); return; }
    await fetch(`${apiUrl}/wealth/items/${encodeURIComponent(id)}`, { method: 'DELETE', headers: auth() }).catch(() => null);
    load();
  };

  const askAi = () => onAsk(de
    ? 'Bitte analysieren Sie mein Gesamtvermögen: Wie hoch ist der Dubai-Anteil, wie sieht es mit Liquidität und Währungen aus, und was sollte ich beachten?'
    : 'Please analyse my whole wealth: how big is the Dubai share, how do liquidity and currencies look, and what should I watch?');

  return (
    <div className="wo">
      <div className="wo-hero">
        <span className="wo-mark"><Wallet size={18} /></span>
        <div className="wo-cur" role="group" aria-label={de ? 'Währung' : 'Currency'}>
          {(['EUR', 'AED'] as const).map(c => (
            <button key={c} type="button" className={showCur === c ? 'is-on' : ''} onClick={() => pickCur(c)} disabled={c === 'AED' && !fx?.aedPerEur}>
              {c === 'EUR' ? '€ EUR' : 'AED'}
            </button>
          ))}
        </div>
        <div className="wo-eyebrow">{de ? 'Asset Desk KI · Gesamtvermögen' : 'Asset Desk AI · Whole wealth'}</div>
        <div className="wo-title">{de ? 'Ihr Vermögen auf einen Blick.' : 'Your wealth at a glance.'}</div>
        {loading ? <div className="wo-lead">…</div> : (
          <>
            <div className="wo-net">{eurFmt(totals.net)}</div>
            <div className="wo-lead">
              {de ? 'Nettovermögen' : 'Net worth'} · {de ? 'Vermögen' : 'assets'} {eurFmt(totals.assets)} · {de ? 'Verbindlichkeiten' : 'liabilities'} {eurFmt(totals.liabilities)}
              {fx?.date ? ` · ${de ? 'EZB-Kurs vom' : 'ECB rate of'} ${fx.date.split('-').reverse().join('.')}` : ''}
            </div>
          </>
        )}
        {!loading && totals.assets > 0 && (
          <div className="wo-bar" aria-hidden="true">
            {totals.assetsList.map(([c, v], i) => <span key={c} style={{ width: `${share(v)}%`, background: SHADES[i % SHADES.length] }} />)}
          </div>
        )}
        {!loading && totals.assets > 0 && (
          <div className="wo-legend">
            {totals.assetsList.map(([c, v], i) => (
              <span key={c}><i style={{ background: SHADES[i % SHADES.length] }} />{cat(c)} {pctFmt(share(v))}</span>
            ))}
          </div>
        )}
      </div>

      {(rentNetEur !== null || airbnbEur !== null) && (
        <section className="wo-card">
          <div className="wo-h">{de ? 'Laufende Einnahmen' : 'Ongoing income'}</div>
          {rentNetEur !== null && (
            <div className="wo-row is-auto">
              <div>
                <b>{de ? 'Langzeitvermietung' : 'Long-term rental'}</b>
                <span>
                  {de
                    ? `${book.rows.length} ${book.rows.length === 1 ? 'Mietvertrag' : 'Mietverträge'} · Miete ${rentGrossEur !== null ? eurFmt(rentGrossEur) : '—'}, netto nach Kosten und Service Charges`
                    : `${book.rows.length} ${book.rows.length === 1 ? 'lease' : 'leases'} · rent ${rentGrossEur !== null ? eurFmt(rentGrossEur) : '—'}, net after costs and service charges`}
                  {book.scUnknown > 0 ? (de ? ` · bei ${book.scUnknown} ohne bekannte Service Charge` : ` · ${book.scUnknown} without a known service charge`) : ''}
                </span>
              </div>
              <div className="wo-amt">{eurFmt(rentNetEur)}</div>
            </div>
          )}
          {airbnbEur !== null && (
            <div className="wo-row is-auto">
              <div>
                <b>Airbnb (DreamHaven)</b>
                <span>
                  {de
                    ? `${airbnbTotals.units} ${airbnbTotals.units === 1 ? 'Apartment' : 'Apartments'}${sinceLabel ? ` · vermietet seit ${sinceLabel}` : ''} · netto an Sie, ${airbnbTotals.months} abgeschlossene Monate${airbnbTotals.occupancy !== null ? ` · Auslastung ${airbnbTotals.occupancy} %` : ''}`
                    : `${airbnbTotals.units} ${airbnbTotals.units === 1 ? 'apartment' : 'apartments'}${sinceLabel ? ` · let since ${sinceLabel}` : ''} · net to you, ${airbnbTotals.months} closed months${airbnbTotals.occupancy !== null ? ` · occupancy ${airbnbTotals.occupancy}%` : ''}`}
                </span>
              </div>
              <div className="wo-amt">{eurFmt(airbnbEur)}</div>
            </div>
          )}
          <div className="wo-row">
            <div>
              <b>{airbnbShort ? (de ? 'Netto-Einnahmen' : 'Net income') : (de ? 'Netto-Einnahmen pro Jahr' : 'Net income per year')}</b>
              <span>
                {airbnbShort
                  ? (de ? `Langzeitmiete pro Jahr + Airbnb der letzten ${airbnbTotals.months} Monate` : `long-term rent per year + Airbnb of the last ${airbnbTotals.months} months`)
                  : (de ? 'aus Ihren Mietverträgen und Airbnb-Abrechnungen' : 'from your leases and Airbnb statements')}
              </span>
            </div>
            <div className="wo-amt">{eurFmt(incomeEur)}</div>
          </div>
          <div className="wo-note">
            {de
              ? `Ihre ${mix.held} Einheiten: ${mix.lease} langfristig vermietet · ${mix.airbnbOnly} nur Airbnb · ${mix.building} im Bau · ${mix.none} ohne erfasste Miete. Einheiten im Bau erzielen noch keine Miete; für die übrigen fehlt ein Mietvertrag in der App.${airbnbShort && sinceLabel ? ` Die Airbnb-Vermietung ist erst seit ${sinceLabel} aktiv – daher zählen dort bisher nur ${airbnbTotals.months} Monate.` : ''}`
              : `Your ${mix.held} units: ${mix.lease} on long-term leases · ${mix.airbnbOnly} Airbnb only · ${mix.building} under construction · ${mix.none} without recorded rent. Units under construction earn no rent yet; for the rest no lease is in the app.${airbnbShort && sinceLabel ? ` Airbnb letting only started in ${sinceLabel}, so only ${airbnbTotals.months} months count there so far.` : ''}`}
          </div>
        </section>
      )}

      {insights.length > 0 && (
        <section className="wo-card">
          <div className="wo-h">{de ? 'Was der Co-Pilot sieht' : 'What the Co-Pilot sees'}</div>
          <ul className="wo-insights">{insights.map(t => <li key={t}>{t}</li>)}</ul>
          <button type="button" className="wo-btn is-gold" onClick={askAi}><MessageCircle size={14} /> {de ? 'Mit der KI besprechen' : 'Discuss with the AI'}</button>
        </section>
      )}

      {!hasOther && !loading && (
        <section className="wo-card is-invite">
          <div className="wo-h">{de ? 'Das ganze Bild – nicht nur Dubai' : 'The whole picture — not only Dubai'}</div>
          <div className="wo-p">
            {de
              ? 'Erfassen Sie, was Sie außerhalb Ihrer Dubai-Immobilien besitzen und schulden – Konten, Wertpapiere, Beteiligungen, Edelmetalle, weitere Immobilien, Kredite. Dann kann Ihr Co-Pilot Konzentration, Liquidität und Währungen für Ihr gesamtes Vermögen beurteilen.'
              : 'Record what you own and owe outside your Dubai property — accounts, securities, holdings, metals, other real estate, loans. Your Co-Pilot can then judge concentration, liquidity and currencies for your whole wealth.'}
          </div>
        </section>
      )}

      <section className="wo-card">
        <div className="wo-h-row">
          <div className="wo-h">{de ? 'Vermögen & Verbindlichkeiten' : 'Assets & liabilities'}</div>
          <button type="button" className="wo-btn" onClick={() => setEditing({ kind: 'asset', category: 'cash', currency: 'EUR' })}><Plus size={14} /> {de ? 'Hinzufügen' : 'Add'}</button>
        </div>
        <div className="wo-row is-auto">
          <div><b>{cat('dubai')}</b><span>{de ? `${dubai.rows.length} Einheiten, aus der App` : `${dubai.rows.length} units, from the app`}</span></div>
          <div className="wo-amt">{toEur(dubai.value, 'AED') !== null ? eurFmt(toEur(dubai.value, 'AED') as number) : '—'}</div>
        </div>
        {openInstalmentsAed > 0 && (
          <div className="wo-row is-auto">
            <div><b>{cat('instalments')}</b><span>{de ? 'aus Ihren Zahlungsplänen' : 'from your payment plans'}</span></div>
            <div className="wo-amt">− {toEur(openInstalmentsAed, 'AED') !== null ? eurFmt(toEur(openInstalmentsAed, 'AED') as number) : '—'}</div>
          </div>
        )}
        {items.map(i => {
          const eur = toEur(i.amount, i.currency);
          return (
            <div key={i.id} className="wo-row">
              <div><b>{i.label || cat(i.category)}</b><span>{cat(i.category)} · {Math.round(i.amount).toLocaleString(locale)} {i.currency}</span></div>
              <div className="wo-amt">{i.kind === 'liability' ? '− ' : ''}{eur !== null ? eurFmt(eur) : (de ? 'kein Kurs' : 'no rate')}</div>
              <div className="wo-acts">
                <button type="button" aria-label={de ? 'Bearbeiten' : 'Edit'} onClick={() => setEditing(i)}><Pencil size={14} /></button>
                <button type="button" aria-label={de ? 'Löschen' : 'Delete'} onClick={() => del(i.id)}><Trash2 size={14} /></button>
              </div>
            </div>
          );
        })}
        <div className="wo-note">
          {de
            ? 'Ihre eigenen Angaben, von uns nicht geprüft. Nur Sie können sie ändern oder löschen; wer sie angesehen hat, sehen Sie im Trust Center. Die KI zeigt Aufteilung und Risiken – sie empfiehlt keine Wertpapiere oder Fonds.'
            : 'Your own entries, not verified by us. Only you can change or delete them; who has viewed them is shown in the Trust Center. The AI shows allocation and risks — it does not recommend securities or funds.'}
        </div>
      </section>

      {editing && (
        <div className="wo-modal" role="dialog" aria-modal="true">
          <div className="wo-sheet">
            <div className="wo-h-row">
              <div className="wo-h">{editing.id ? (de ? 'Eintrag bearbeiten' : 'Edit entry') : (de ? 'Eintrag hinzufügen' : 'Add entry')}</div>
              <button type="button" className="wo-x" aria-label="Close" onClick={() => setEditing(null)}><X size={18} /></button>
            </div>
            <div className="wo-seg">
              {(['asset', 'liability'] as const).map(k => (
                <button key={k} type="button" className={editing.kind === k ? 'is-on' : ''}
                  onClick={() => setEditing({ ...editing, kind: k, category: k === 'asset' ? 'cash' : 'loan' })}>
                  {k === 'asset' ? (de ? 'Vermögen' : 'Asset') : (de ? 'Verbindlichkeit' : 'Liability')}
                </button>
              ))}
            </div>
            <label>{de ? 'Art' : 'Type'}
              <select value={editing.category} onChange={e => setEditing({ ...editing, category: e.target.value })}>
                {(editing.kind === 'liability' ? LIAB_CATS : ASSET_CATS).map(c => <option key={c} value={c}>{cat(c)}</option>)}
              </select>
            </label>
            <label>{de ? 'Bezeichnung (optional)' : 'Name (optional)'}
              <input value={editing.label || ''} maxLength={80} onChange={e => setEditing({ ...editing, label: e.target.value })} placeholder={de ? 'z. B. Depot Sparkasse' : 'e.g. brokerage account'} />
            </label>
            <div className="wo-two">
              <label>{de ? 'Betrag' : 'Amount'}
                <input inputMode="decimal" value={editing.amount ?? ''} onChange={e => setEditing({ ...editing, amount: e.target.value as any })} placeholder="250000" />
              </label>
              <label>{de ? 'Währung' : 'Currency'}
                <select value={editing.currency} onChange={e => setEditing({ ...editing, currency: e.target.value })}>
                  {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </label>
            </div>
            <button type="button" className="wo-btn is-gold is-wide" disabled={saving} onClick={save}>{de ? 'Speichern' : 'Save'}</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default WealthOverview;
